import {
  format,
  isValid,
} from "date-fns";

export const is = {
  null: (value) => value === null,
  undefined: (value) => value === undefined,
  nullOrUndefined: (value) => is.undefined(value) || is.null(value),
  string: (value) => typeof value === "string",
  object: (value) => typeof value === "object",
  array: (value) => Array.isArray(value),
  empty: (value) => {
    if (is.nullOrUndefined(value)) {
      return true;
    }
    if (is.array(value) || is.string(value)) {
      return value.length === 0;
    }
    return is.object(value) && Object.keys(value).length === 0;
  },
};

// Utility function to combine class names (similar to cn from shadcn)
export const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

export const toast = {
  success: (message) => {
    console.log('Success:', message);
    showMuiToast(message, 'success');
  },
  error: (message) => {
    console.log('Error:', message);
    showMuiToast(message, 'error');
  },
  info: (message) => {
    console.log('Info:', message);
    showMuiToast(message, 'info');
  },
  warning: (message) => {
    console.log('Warning:', message);
    showMuiToast(message, 'warning');
  }
};

// Create MUI Snackbar toast
const showMuiToast = (message, severity = 'info') => {
  // Import React and ReactDOM dynamically
  import('react').then(React => {
    import('react-dom/client').then(ReactDOM => {
      import('@mui/material').then(({ Snackbar, Alert }) => {
        
        // Remove existing toast container
        const existingContainer = document.getElementById('toast-container');
        if (existingContainer) {
          existingContainer.remove();
        }
        
        // Create new toast container
        const container = document.createElement('div');
        container.id = 'toast-container';
        document.body.appendChild(container);
        
        const root = ReactDOM.createRoot(container);
        
        const ToastComponent = () => {
          const [open, setOpen] = React.useState(true);
          
          const handleClose = (event, reason) => {
            if (reason === 'clickaway') {
              return;
            }
            setOpen(false);
            
            // Clean up after animation
            setTimeout(() => {
              if (container.parentNode) {
                container.remove();
              }
            }, 200);
          };
          
          return React.createElement(Snackbar, {
            open,
            autoHideDuration: 4000,
            onClose: handleClose,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            sx: { 
              mt: 2,
              '& .MuiSnackbar-root': {
                zIndex: 9999
              }
            }
          }, React.createElement(Alert, {
            onClose: handleClose,
            severity,
            variant: 'filled',
            sx: { 
              width: '100%',
              maxWidth: '400px',
              fontWeight: 500
            }
          }, message));
        };
        
        root.render(React.createElement(ToastComponent));
      });
    });
  });
};

// Utility functions for the charging demo
export const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case 'available': return '#4caf50';
    case 'charging': return '#2196f3';
    case 'faulted': return '#f44336';
    case 'preparing': return '#4caf50';
    case 'unavailable': return '#f44336';
    default: return '#9e9e9e';
  }
};

export const getStatusBadgeColor = (status) => {
  switch (status) {
    case 'available': return 'success';
    case 'charging': return 'info';
    case 'faulted': return 'error';
    default: return 'default';
  }
};

// Connector status utilities
export const getConnectorStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'available':
      return '#4caf50';
    case 'charging':
      return '#2196f3';
    case 'unavailable':
      return '#f44336';
    case 'preparing':
      return '#4caf50';
    case 'faulted':
      return '#d32f2f';
    default:
      return '#9e9e9e';
  }
};

export const getConnectorStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'available':
      return '●'; // Available dot
    case 'charging':
      return '⚡'; // Charging bolt
    case 'unavailable':
      return '●'; // Unavailable dot
    case 'preparing':
      return '◐'; // Preparing half-circle
    case 'faulted':
      return '⚠'; // Warning
    default:
      return '○'; // Unknown circle
  }
};

export function fDateTime(date, dateFormat = "dd MMM yyyy HH:mm") {
  const parsedDate = new Date(date);
  return !is.empty(date) && isValid(parsedDate)
    ? format(parsedDate, dateFormat)
    : "";
}